/**
 * useHreflang — imperatively injects <link rel="alternate" hreflang="xx">
 * elements into the document <head> for every supported locale, plus an
 * x-default canonical. No react-helmet dependency — mirrors the pattern
 * already used by useSeoMeta.js.
 *
 * Usage:
 *   useHreflang();                   // landing (/)
 *   useHreflang('bookstore');        // for /bookstore, /fr/bookstore, etc.
 *   useHreflang('how-we-help-writers'); // etc.
 *
 * The optional `enabled` flag (default true) allows conditional injection
 * without violating the Rules of Hooks — pass false to skip injection
 * while still satisfying hook call requirements.
 *
 * useHreflangAlternates(alternates) — for pages where each locale has a
 * different URL/slug (e.g. auto-blog posts). Pass a map:
 *   { en: '/blog/slug-en', fr: '/fr/blog/slug-fr', ... }
 * emits exact hreflang links for those URLs.
 */
import { useEffect } from 'react';
import { SUPPORTED_LANGUAGES } from '@/i18n/config';

/**
 * Maps a locale code to its canonical URL path on this domain.
 * English lives at root `/`; every other language gets its own prefix.
 */
export const HREFLANG_PATHS = {
  en: '',       // → /
  fr: '/fr',
  de: '/de',
  es: '/es',
  pt: '/pt',
  zh: '/zh',
};

export function useHreflang(subpath = '', enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const base = window.location.origin;
    // suffix always starts with '/' or is '' (no slash)
    const suffix = subpath ? `/${subpath.replace(/^\//, '')}` : '';
    const injected = [];

    // SEO audit fix — update <html lang=""> so screen-readers and
    // crawlers see the correct language on locale routes (/fr, /de …).
    const prevHtmlLang = document.documentElement.lang;
    const localeMatch = window.location.pathname.match(/^\/(fr|de|es|pt|zh)(\/|$)/);
    document.documentElement.lang = localeMatch ? localeMatch[1] : 'en';

    // x-default → English canonical (suffix already contains leading slash)
    const xdef = createAlternate('x-default', `${base}${suffix}`);
    document.head.appendChild(xdef);
    injected.push(xdef);

    for (const lang of SUPPORTED_LANGUAGES) {
      const prefix = HREFLANG_PATHS[lang.code] ?? `/${lang.code}`;
      const el = createAlternate(lang.code, `${base}${prefix}${suffix}`);
      document.head.appendChild(el);
      injected.push(el);
    }

    return () => {
      for (const el of injected) {
        el.parentNode?.removeChild(el);
      }
      document.documentElement.lang = prevHtmlLang;
    };
    // Re-runs when subpath or enabled changes so dynamic routes (e.g. /blog/:slug)
    // correctly update hreflang without a full unmount/remount cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subpath, enabled]);
}

/**
 * useHreflangAlternates — per-post hreflang for pages where each locale
 * has a distinct URL (e.g. auto-blog posts with locale-specific slugs).
 *
 * alternates: { en: '/blog/slug', fr: '/fr/blog/fr-slug', ... }
 * Pass null/undefined to skip (does nothing, safe for hook ordering).
 * 2026-02 — added for blog post cross-locale SEO.
 */
export function useHreflangAlternates(alternates) {
  // JSON-stringify so the effect re-fires when the object contents change
  // (object identity changes on every render otherwise).
  const altsKey = alternates ? JSON.stringify(alternates) : '';
  useEffect(() => {
    if (!alternates || Object.keys(alternates).length === 0) return;
    const base = window.location.origin;
    const injected = [];

    for (const [hreflang, path] of Object.entries(alternates)) {
      const href = path.startsWith('http') ? path : `${base}${path}`;
      const el = createAlternate(hreflang, href);
      document.head.appendChild(el);
      injected.push(el);
    }

    return () => {
      for (const el of injected) el.parentNode?.removeChild(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [altsKey]);
}

function createAlternate(hreflang, href) {
  const el = document.createElement('link');
  el.rel       = 'alternate';
  el.hreflang  = hreflang;
  el.href      = href;
  return el;
}
